import React from 'react'
import styled from 'styled-components'
import type { TFunction } from 'i18next'
import { Icon } from '../../../atoms/Icon'
import { getComplianceDeadline, getIDCStatus } from '../utils/idcCalculations'

interface IDCRegulatoryBannerProps {
  idc: number // MJ/m²·year
  currentYear?: number
  t: TFunction
}

const Banner = styled.div<{
  $urgency: 'none' | 'low' | 'medium' | 'high' | 'critical'
}>`
  background: ${({ theme }) => theme.colors.background.primary};
  border-left: 4px solid
    ${({ $urgency }) => {
      switch ($urgency) {
        case 'critical':
          return '#ef4444'
        case 'high':
          return '#f97316'
        case 'medium':
          return '#f59e0b'
        case 'low':
          return '#eab308'
        default:
          return '#10b981'
      }
    }};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: 20px 24px;
  margin-bottom: 24px;
`

const BannerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`

const IconWrapper = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};
  flex-shrink: 0;
  margin-top: 2px;
`

const HeaderContent = styled.div`
  flex: 1;
`

const Title = styled.h4<{ $color: string }>`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ $color }) => $color};
  margin: 0 0 4px 0;
`

const Message = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  line-height: 1.5;
`

const DeadlineHighlight = styled.span<{ $color: string }>`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ $color }) => $color};
`

const getRegulatoryInfo = (
  urgency: 'none' | 'low' | 'medium' | 'high' | 'critical',
  actionType: string,
  t: TFunction
) => {
  const iconColor = {
    critical: '#ef4444',
    high: '#f97316',
    medium: '#f59e0b',
    low: '#eab308',
    none: '#10b981',
  }[urgency]

  const icon = {
    critical: 'alert-triangle',
    high: 'alert-circle',
    medium: 'info',
    low: 'clock',
    none: 'check-circle',
  }[urgency]

  let title = ''

  switch (actionType) {
    case 'mandatory_renovation':
      title = t('idcRegulatoryBanner.actionTypes.mandatoryRenovation')
      break

    case 'audit':
      title = t('idcRegulatoryBanner.actionTypes.audit')
      break

    case 'optimization':
      title = t('idcRegulatoryBanner.actionTypes.optimization')
      break

    default:
      title = t('idcRegulatoryBanner.actionTypes.noObligation')
  }

  return { iconColor, icon, title }
}

export const IDCRegulatoryBanner: React.FC<IDCRegulatoryBannerProps> = ({
  idc,
  currentYear = new Date().getFullYear(),
  t,
}) => {
  const status = getIDCStatus(idc, currentYear)
  const deadline = getComplianceDeadline(idc, currentYear)
  const { iconColor, icon, title } = getRegulatoryInfo(
    deadline.urgency,
    status.actionType,
    t
  )

  const getYearsRemainingText = (count: number) => {
    if (count === 1) {
      return t('idcRegulatoryBanner.yearsRemaining.one', { count })
    }
    return t('idcRegulatoryBanner.yearsRemaining.other', { count })
  }

  return (
    <Banner $urgency={deadline.urgency}>
      <BannerHeader>
        <IconWrapper $color={iconColor}>
          <Icon name={icon as any} size="medium" />
        </IconWrapper>
        <HeaderContent>
          <Title $color={iconColor}>{title}</Title>
          <Message>
            {deadline.message}
            {deadline.hasDeadline && deadline.year && (
              <>
                {' '}
                <DeadlineHighlight $color={iconColor}>
                  {t('idcRegulatoryBanner.deadline', {
                    year: deadline.year,
                  })}
                  {deadline.yearsRemaining !== undefined &&
                    deadline.yearsRemaining > 0 && (
                      <> {getYearsRemainingText(deadline.yearsRemaining)}</>
                    )}
                </DeadlineHighlight>
              </>
            )}
          </Message>
        </HeaderContent>
      </BannerHeader>
    </Banner>
  )
}
